import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Icons
const BackIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>);
const CloseIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>);
const LocationIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="12" r="3"/></svg>);
const PhoneIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.574 2.81.7A2 2 0 0 1 22 16.92z"/></svg>);
const CalendarIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>);
const CreditCardIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>);
const PackageIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>);
const StoreIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>);
const UserIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>);
const MoneyIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>);
const TruckIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="6" width="18" height="12" rx="2"/><circle cx="6" cy="18" r="2"/><circle cx="18" cy="18" r="2"/></svg>);
const CheckCircleIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>);
const ClockIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>);
const CircleIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="8"/></svg>);

const StatusBadge = ({ status, isDark }) => {
  const getConfig = () => {
    switch(status?.toLowerCase()) {
      case "completed": return { bg: "rgba(34,197,94,0.15)", color: "#22c55e" };
      case "pending": return { bg: "rgba(245,158,11,0.15)", color: "#f59e0b" };
      case "cancelled": return { bg: "rgba(239,68,68,0.15)", color: "#ef4444" };
      case "accepted": return { bg: "rgba(59,130,246,0.15)", color: "#3b82f6" };
      case "preparing": return { bg: "rgba(168,85,247,0.15)", color: "#a855f7" };
      case "ready": return { bg: "rgba(236,72,153,0.15)", color: "#ec4899" };
      case "out_for_delivery": return { bg: "rgba(6,182,212,0.15)", color: "#06b6d4" };
      case "delivered": return { bg: "rgba(34,197,94,0.15)", color: "#22c55e" };
      case "dropped": return { bg: "rgba(107,114,128,0.15)", color: "#6b7280" };
      default: return { bg: "rgba(148,163,184,0.12)", color: "#94a3b8" };
    }
  };
  const c = getConfig();
  return (
    <span style={{
      display: "inline-block", padding: "4px 12px", borderRadius: "20px",
      fontSize: "12px", fontWeight: 600, background: c.bg, color: c.color,
    }}>{status}</span>
  );
};

// Redesigned Vertical Timeline Event Component
const VerticalTimelineEvent = ({ label, value, isCompleted, isLast, ts, suffix = "", icon }) => {
  return (
    <div style={{ display: "flex", position: "relative", marginBottom: isLast ? 0 : "24px" }}>
      {/* Timeline Line & Icon */}
      <div style={{ position: "relative", width: "60px", flexShrink: 0 }}>
        {/* Vertical connecting line */}
        {!isLast && (
          <div style={{
            position: "absolute",
            top: "32px",
            left: "28px",
            width: "2px",
            height: "calc(100% + 8px)",
            background: isCompleted ? ts.success : `${ts.border}`,
            zIndex: 0
          }} />
        )}
        
        {/* Icon Circle */}
        <div style={{
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          background: isCompleted ? ts.success : ts.surfaceLighter,
          border: isCompleted ? `2px solid ${ts.success}` : `2px solid ${ts.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          zIndex: 1,
          boxShadow: isCompleted ? `0 0 0 4px ${ts.success}20` : "none"
        }}>
          {isCompleted ? (
            <CheckCircleIcon style={{ width: "20px", height: "20px", color: "#fff" }} />
          ) : (
            <CircleIcon style={{ width: "12px", height: "12px", color: ts.textMuted }} />
          )}
        </div>
      </div>
      
      {/* Content */}
      <div style={{ flex: 1, paddingLeft: "16px", paddingBottom: "8px" }}>
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          flexWrap: "wrap", 
          gap: "8px",
          marginBottom: "6px"
        }}>
          <span style={{ 
            fontSize: "14px", 
            fontWeight: 600, 
            color: isCompleted ? ts.success : ts.textPrimary 
          }}>
            {label}
          </span>
          {isCompleted && (
            <span style={{
              fontSize: "10px",
              padding: "2px 8px",
              borderRadius: "12px",
              background: `${ts.success}20`,
              color: ts.success,
              fontWeight: 500
            }}>
              Completed
            </span>
          )}
          {!isCompleted && value === "—" && (
            <span style={{
              fontSize: "10px",
              padding: "2px 8px",
              borderRadius: "12px",
              background: `${ts.warning}20`,
              color: ts.warning,
              fontWeight: 500
            }}>
              Pending
            </span>
          )}
        </div>
        
        {value && value !== "—" ? (
          <>
            <div style={{ 
              fontSize: "13px", 
              fontWeight: 500, 
              color: ts.textPrimary,
              fontFamily: "'Monaco', monospace",
              marginBottom: "4px"
            }}>
              {value}
            </div>
            {suffix && (
              <div style={{ 
                fontSize: "11px", 
                color: ts.textMuted,
                display: "flex",
                alignItems: "center",
                gap: "4px",
                marginTop: "2px"
              }}>
                <ClockIcon style={{ width: "10px", height: "10px" }} />
                {suffix}
              </div>
            )}
          </>
        ) : (
          <div style={{ 
            fontSize: "12px", 
            color: ts.textMuted,
            fontStyle: "italic"
          }}>
            Waiting for update...
          </div>
        )}
      </div>
    </div>
  );
};

const OrderDetailPage = ({ isDark = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const order = location.state?.order;

  const ts = {
    background: isDark ? "#0d1117" : "#f8fafc",
    surface: isDark ? "#141824" : "#ffffff",
    surfaceLighter: isDark ? "#1a2035" : "#f1f5f9",
    border: isDark ? "#1e2740" : "#e2e8f0",
    textPrimary: isDark ? "#f1f5f9" : "#0f172a",
    textSecondary: isDark ? "#94a3b8" : "#334155",
    textMuted: isDark ? "#64748b" : "#64748b",
    primary: "#3b82f6",
    success: "#22c55e",
    error: "#ef4444",
    warning: "#f59e0b",
  };

  // Parse items from JSON string
  const parseItems = (itemsStr) => {
    try {
      if (typeof itemsStr === 'string') {
        return JSON.parse(itemsStr);
      }
      return itemsStr || [];
    } catch {
      return [];
    }
  };

  const items = parseItems(order?.items);
  
  // Calculate totals
  const itemTotal = items.reduce((sum, item) => sum + (item.price * (item.qty || 1)), 0);
  const finalAmount = parseFloat(order?.final_amount) || parseFloat(order?.price) || 0;
  const discount = parseFloat(order?.discount) || 0;
  const tax = parseFloat(order?.tax) || 0;
  const deliveryCharge = parseFloat(order?.delivery_charge) || 0;
  const handlingCharge = parseFloat(order?.handling_charge) || 0;
  const platformFee = parseFloat(order?.user_platform_fee) || parseFloat(order?.business_platform_fee) || 0;
  const driverAmount = parseFloat(order?.driver_amount) || 0;
  const businessAmount = parseFloat(order?.business_amount) || 0;

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString || dateString === "null" || dateString === null) return null;
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return dateString;
    }
  };

  // Get cancel reason based on who cancelled
  const getCancelReason = () => {
    if (order?.user_cancel_time && order?.user_cancel_remarks) {
      return { type: "User Cancel", time: order.user_cancel_time, reason: order.user_cancel_remarks };
    }
    if (order?.business_cancel_time && order?.business_cancel_remarks) {
      return { type: "Business Cancel", time: order.business_cancel_time, reason: order.business_cancel_remarks };
    }
    if (order?.driver_cancel_time && order?.driver_cancel_remarks) {
      return { type: "Driver Cancel", time: order.driver_cancel_time, reason: order.driver_cancel_remarks };
    }
    return null;
  };

  const cancelInfo = getCancelReason();

  // Prepare timeline events array
  const timelineEvents = [
    { 
      label: "Order Placed", 
      value: formatDate(order?.created_at), 
      isCompleted: !!order?.created_at,
      suffix: ""
    },
    { 
      label: "Restaurant Accepted", 
      value: formatDate(order?.restro_accept_time), 
      isCompleted: !!order?.restro_accept_time,
      suffix: order?.restro_prep_duration ? `Prep Duration: ${order.restro_prep_duration}` : ""
    },
    { 
      label: "Restaurant Ready", 
      value: formatDate(order?.restro_ready_time), 
      isCompleted: !!order?.restro_ready_time,
      suffix: ""
    },
    { 
      label: "Rider Pickup", 
      value: formatDate(order?.rider_pickup_time), 
      isCompleted: !!order?.rider_pickup_time,
      suffix: ""
    },
    { 
      label: "Order Delivered", 
      value: formatDate(order?.order_delivered_time), 
      isCompleted: !!order?.order_delivered_time,
      suffix: ""
    }
  ];

  // Add cancel event if exists
  if (cancelInfo) {
    timelineEvents.push({
      label: cancelInfo.type,
      value: formatDate(cancelInfo.time),
      isCompleted: true,
      suffix: cancelInfo.reason
    });
  }

  const styles = {
    container: {
      minHeight: "100vh",
      background: ts.background,
      padding: "24px",
      fontFamily: "'Inter', sans-serif",
    },
    backButton: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      background: "none",
      border: "none",
      color: ts.primary,
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: 500,
      marginBottom: "24px",
      padding: "8px 0",
    },
    header: {
      marginBottom: "24px",
    },
    title: {
      fontSize: "28px",
      fontWeight: 700,
      color: ts.textPrimary,
      margin: 0,
    },
    orderId: {
      fontSize: "14px",
      color: ts.textMuted,
      marginTop: "4px",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
      gap: "20px",
    },
    card: {
      background: ts.surface,
      border: `1px solid ${ts.border}`,
      borderRadius: "16px",
      overflow: "hidden",
    },
    cardHeader: {
      padding: "16px 20px",
      borderBottom: `1px solid ${ts.border}`,
      background: ts.surfaceLighter,
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    cardTitle: {
      fontSize: "16px",
      fontWeight: 600,
      color: ts.textPrimary,
      margin: 0,
    },
    cardBody: {
      padding: "20px",
    },
    infoRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "10px 0",
      borderBottom: `1px solid ${ts.border}`,
    },
    infoLabel: {
      fontSize: "12px",
      color: ts.textMuted,
    },
    infoValue: {
      fontSize: "13px",
      fontWeight: 500,
      color: ts.textPrimary,
    },
    itemRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "12px 0",
      borderBottom: `1px solid ${ts.border}`,
    },
    itemName: {
      fontSize: "13px",
      fontWeight: 500,
      color: ts.textPrimary,
    },
    itemQty: {
      fontSize: "11px",
      color: ts.textMuted,
    },
    itemPrice: {
      fontSize: "13px",
      fontWeight: 600,
      color: ts.textPrimary,
    },
    totalRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "12px 0",
      fontWeight: 700,
      color: ts.textPrimary,
    },
    statusContainer: {
      display: "flex",
      gap: "12px",
      flexWrap: "wrap",
      marginBottom: "16px",
    },
    addressText: {
      fontSize: "13px",
      lineHeight: "1.5",
      color: ts.textSecondary,
    },
    noData: {
      textAlign: "center",
      padding: "40px",
      color: ts.textMuted,
    },
    // Vertical Timeline styles
    timelineContainer: {
      maxHeight: "500px",
      overflowY: "auto",
      paddingRight: "8px",
    },
    cancelReasonBox: {
      marginTop: "16px",
      padding: "12px",
      background: "rgba(239,68,68,0.1)",
      borderRadius: "8px",
      border: `1px solid ${ts.error}`,
    },
    cancelReasonText: {
      fontSize: "12px",
      color: ts.error,
    },
  };

  if (!order) {
    return (
      <div style={styles.container}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <BackIcon /> Back to Orders
        </button>
        <div style={styles.noData}>No order data available</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <button style={styles.backButton} onClick={() => navigate(-1)}>
        <BackIcon /> Back to Orders
      </button>

      <div style={styles.header}>
        <h1 style={styles.title}>Order Details</h1>
        <p style={styles.orderId}>Order #{order.id} • {new Date(order.created_at).toLocaleString()}</p>
      </div>

      <div style={styles.grid}>
        {/* Order Summary Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <PackageIcon style={{ color: ts.primary }} />
            <h3 style={styles.cardTitle}>Order Summary</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.statusContainer}>
              <StatusBadge status={order.status} isDark={isDark} />
              <StatusBadge status={order.order_status_restro} isDark={isDark} />
              <StatusBadge status={order.order_status_driver} isDark={isDark} />
            </div>
            
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Order Status</span>
              <span style={styles.infoValue}>{order.status || "-"}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Restaurant Status</span>
              <span style={styles.infoValue}>{order.order_status_restro || "-"}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Driver Status</span>
              <span style={styles.infoValue}>{order.order_status_driver || "-"}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>User Status</span>
              <span style={styles.infoValue}>{order.order_status_user || "-"}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Delivery Person ID</span>
              <span style={styles.infoValue}>{order.delivery_person_id || "Not assigned"}</span>
            </div>
          </div>
        </div>

        {/* Customer Information Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <UserIcon style={{ color: ts.primary }} />
            <h3 style={styles.cardTitle}>Customer Information</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Customer Name</span>
              <span style={styles.infoValue}>{order.receiver_name || "-"}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Phone Number</span>
              <span style={styles.infoValue}>{order.receiver_phone || "-"}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>User ID</span>
              <span style={styles.infoValue}>{order.userid || "-"}</span>
            </div>
          </div>
        </div>

        {/* Restaurant Information Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <StoreIcon style={{ color: ts.primary }} />
            <h3 style={styles.cardTitle}>Restaurant Information</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Restaurant Name</span>
              <span style={styles.infoValue}>{order.designation || "-"}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Designation ID</span>
              <span style={styles.infoValue}>{order.designation_id || "-"}</span>
            </div>
          </div>
        </div>

        {/* Payment Details Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <CreditCardIcon style={{ color: ts.primary }} />
            <h3 style={styles.cardTitle}>Payment Details</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Payment Status</span>
              <span style={styles.infoValue}>
                <StatusBadge status={order.pay_status} isDark={isDark} />
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Payment Method</span>
              <span style={styles.infoValue}>{order.pay_method || "-"}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Item Total</span>
              <span style={styles.infoValue}>₹{itemTotal}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Discount</span>
              <span style={styles.infoValue}>-₹{discount}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Tax</span>
              <span style={styles.infoValue}>₹{tax}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Delivery Charge</span>
              <span style={styles.infoValue}>₹{deliveryCharge}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Handling Charge</span>
              <span style={styles.infoValue}>₹{handlingCharge}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Platform Fee</span>
              <span style={styles.infoValue}>₹{platformFee}</span>
            </div>
            <div style={styles.totalRow}>
              <span>Final Amount</span>
              <span style={{ color: ts.success, fontSize: "18px" }}>₹{finalAmount}</span>
            </div>
          </div>
        </div>

        {/* Delivery Address Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <LocationIcon style={{ color: ts.primary }} />
            <h3 style={styles.cardTitle}>Delivery Address</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Dropoff Address</span>
              <span style={styles.addressText}>{order.dropoff_address || "-"}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Pickup Address</span>
              <span style={styles.addressText}>{order.pickup_address || "-"}</span>
            </div>
          </div>
        </div>

        {/* Items Ordered Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <PackageIcon style={{ color: ts.primary }} />
            <h3 style={styles.cardTitle}>Items Ordered</h3>
          </div>
          <div style={styles.cardBody}>
            {items.length === 0 ? (
              <div style={styles.noData}>No items found</div>
            ) : (
              items.map((item, idx) => (
                <div key={idx} style={styles.itemRow}>
                  <div>
                    <div style={styles.itemName}>{item.name}</div>
                    <div style={styles.itemQty}>Quantity: {item.qty || 1}</div>
                    {item.variant_name && <div style={styles.itemQty}>Variant: {item.variant_name}</div>}
                  </div>
                  <div style={styles.itemPrice}>₹{item.price * (item.qty || 1)}</div>
                </div>
              ))
            )}
            <div style={styles.totalRow}>
              <span>Total</span>
              <span>₹{itemTotal}</span>
            </div>
          </div>
        </div>

        {/* Financial Breakdown Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <MoneyIcon style={{ color: ts.primary }} />
            <h3 style={styles.cardTitle}>Financial Breakdown</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Business Amount</span>
              <span style={styles.infoValue}>₹{businessAmount}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Driver Amount</span>
              <span style={styles.infoValue}>₹{driverAmount}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Platform Amount</span>
              <span style={styles.infoValue}>₹{order?.platform_amount || 0}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Delivery Charge</span>
              <span style={styles.infoValue}>₹{deliveryCharge}</span>
            </div>
          </div>
        </div>

        {/* Timeline Card - Vertical Design - One event per line */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <CalendarIcon style={{ color: ts.primary }} />
            <h3 style={styles.cardTitle}>Order Timeline</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.timelineContainer}>
              {timelineEvents.map((event, idx) => (
                <VerticalTimelineEvent
                  key={idx}
                  label={event.label}
                  value={event.value || "—"}
                  isCompleted={event.isCompleted}
                  isLast={idx === timelineEvents.length - 1}
                  ts={ts}
                  suffix={event.suffix}
                />
              ))}
            </div>

            {/* Cancel Reason Section (if already not shown in timeline) */}
            {cancelInfo && !timelineEvents.some(e => e.label === cancelInfo.type) && (
              <div style={styles.cancelReasonBox}>
                <span style={styles.cancelReasonText}>
                  <strong>{cancelInfo.type}:</strong> {cancelInfo.reason || "No reason provided"}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;